<script setup lang="ts">
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { isEmpty } from "@core/utils";
import { mobileValidator, emailValidator, requiredValidator } from "@validators";
import { VForm } from "vuetify/components";
import type { BookingStepProps } from "../types/index";
import BookingExtras from "./BookingExtras.vue";
import { getBookingOptionQuantity } from "@/utils/bookingOptionQuantities";
const props = defineProps<BookingStepProps>();

const emit = defineEmits<{
  (e: "update:form", value: any): void;
  (e: "validate"): Promise<boolean>;
  (e: "updateErrorMessage", value: any): void;
}>();

const membersFormRef = ref<InstanceType<typeof VForm>>();
const userStore = useUserStore();
const { currentLocation, currentLocationDetails } = storeToRefs(userStore);

const selectedActivity = computed(() =>
  props.activityList?.find((el) => el.id === props.form.activity)
);

const productList = computed(() => {
  return (selectedActivity.value?.expand?.options || []).filter(
    (option: any) => option.location === currentLocation.value && !option.inactive
  );
});

const checkItemAvailability = async (optionId: string, quantity = 1) => {
  const option = (selectedActivity.value?.expand?.options || []).find(
    (opt: any) => opt.id === optionId
  );
  if (!option) {
    return false;
  }
  if (option?.inStock === 0) {
    emit("updateErrorMessage", {
      isVisible: true,
      message: "Sorry out of stock on this item",
    });
    return false;
  }

  if (option?.hiredOutItem) {
    const bookingStart = moment(
        `${props.form.date} ${props.form.time}`,
        "YYYY-MM-DD HH:mm"
      )
      .add(1, "seconds");
    const bookingSpacing = selectedActivity.value?.bookingSpacing || 60; // Default to 60 minutes if due is not set
    const bookingEnd = bookingStart
      .clone()
      .add(Number(props.form.duration || 1) * bookingSpacing, "minutes")
      .add(-1, "seconds");
    try {
      const bookings = await pb.collection("bookings").getList(1, 50, {
        filter: `startTime < '${bookingEnd
          .utc()
          .format("YYYY-MM-DD HH:mm:ss")}' && endTime > '${bookingStart
          .utc()
          .format("YYYY-MM-DD HH:mm:ss")}'`,
        expand: "docketLine",
      });

      let bookedCount = 0;
      for (const booking of bookings.items) {
        if (booking.options?.length) {
          if (booking.options.includes(optionId) && booking.id !== props.form.id && booking.state !== "Deleted") {
            bookedCount += getBookingOptionQuantity(booking.optionQuantities, optionId);
          }
        }
      }

      if (bookedCount + quantity > option.inStock) {
        emit("updateErrorMessage", {
          isVisible: true,
          message: "Hire Item not available for the booking time selected",
        });
        return false;
      }
    } catch (error) {
      console.error("Error checking availability:", error);
      emit("updateErrorMessage", {
        isVisible: true,
        message: "Error checking availability",
      });
      return false;
    }
  }
  return true;
};

defineExpose({
  validate: async () => {
    const results = await membersFormRef.value?.validate();

    return results?.valid ?? false;
  },
});

const LocalEmailValidator = (value: unknown) => {
  if (isEmpty(value)) return currentLocationDetails.value?.guestValidateEmail ? 'You must provide a valid email' : true;
  return emailValidator(value);
};

const LocalMobileValidator = (value: unknown) => {
  return mobileValidator(value, currentLocationDetails.value?.guestValidateSMS);
};

const canHire = ref(false)

onMounted(async ()=>{
  membersFormRef.value?.validate();

  if(props?.form?.activity && props?.form?.time && props?.form?.date){
    // Get Activity Times
    let allActiveTimes:any = await pb.collection('activities').getOne(props?.form?.activity, {expand:'availibleTimes'})
    if(allActiveTimes && allActiveTimes?.expand?.availibleTimes){
      canHire.value = allActiveTimes.expand.availibleTimes.filter((at: any) => {
        if (!at?.allUsers) {
          return false
        }
        let validFrom = moment(at?.validFrom)
        let validTill = moment(at?.validTill)
        let nowTime = moment(props?.form?.date)

        if ((at?.validFrom != '' && validFrom.isAfter(nowTime)) || (at?.validTill != '' && validTill.isBefore(nowTime))) {
          return false
        }
        let dayCheck = nowTime.format('dddd').toString().toLowerCase()

        if (!at[dayCheck]) {
          return false
        }

        let hour = parseInt(props.form?.time || 0)

        if (hour >= at.startHour && hour <= at.finishHour) {
          return true
        }
        return false
      }).length > 0
    }
  }
})
</script>

<template>
  <VForm ref="membersFormRef">
    <VRow v-if="isAdmin || !loggedUser?.id">
      <!-- Booking Name -->
      <VCol
        cols="12">
        <VTextField
          v-model="form.guests"
          label="Booking Name"
          :rules="[requiredValidator]"
          @update:modelValue="emit('update:form', { ...props.form, guests: [$event] })"/>
      </VCol>

      <!-- Booking Email -->
      <VCol
        cols="6">
        <VTextField
          v-model="form.visitorEmail"
          label="Email"
          :rules="[LocalEmailValidator]"
          placeholder="Please enter your email."
          @update:modelValue="
            emit('update:form', { ...props.form, visitorEmail: $event })
          "
        />
      </VCol>
      <!-- Booking Phone -->
      <VCol
        cols="6">
        <VTextField
          v-model="form.visitorPhone"
          label="Phone"
          :rules="[LocalMobileValidator]"
          placeholder="Please enter a contact number."
          @keyup="form.visitorPhone = form.visitorPhone.replace(/\D/g,'')"
          @update:modelValue="
            emit('update:form', { ...props.form, visitorPhone : $event })
          "
        />
      </VCol>
    </VRow>


      <VRow>
      <!-- Activity Options -->
      <VCol cols="12" v-if="productList?.length && canHire">
        <div>
          <div class="text-subtitle-1 mb-2">Available Extras:</div>
          <BookingExtras
            v-model:selected-options="form.selectedOptions"
            v-model:option-quantities="form.optionQuantities"
            :options="productList"
            :disabled="isFieldsDisabled"
            :check-availability="checkItemAvailability"
            @change="emit('update:form', { ...props.form })"
          />
        </div>
      </VCol>
      <v-col v-else cols="12">
        <div class="text-subtitle-1">
          {{ !productList?.length ? "No Extras available" : "No Extras available during unstaffed hours" }}
        </div>
      </v-col>

      <!-- Notes -->
      <VCol cols="12">
        <VTextarea
          v-model="form.notes"
          label="Booking Notes"
          rows="2"
          @update:modelValue="emit('update:form', { ...form, notes: $event })"
        />
      </VCol>
    </VRow>
  </VForm>
</template>
